import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  useTheme,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  Checkbox,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";





function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join("");
}

function PrimaryBadge() {
  return (
    <Box
      sx={{
        px: 0.7,
        py: "1px",
        borderRadius: 1,
        fontSize: 10,
        fontWeight: 600,
        background: "rgba(214,158,46,0.18)",
        color: "#8a6420",
        lineHeight: 1.4,
      }}
    >
      Primär
    </Box>
  );
}

function RecentBadge() {
  return (
    <Box
      sx={{
        px: 0.7,
        py: "1px",
        borderRadius: 1,
        fontSize: 10,
        fontWeight: 500,
        background: "rgba(15,23,42,0.05)",
        color: "rgba(15,23,42,0.6)",
        lineHeight: 1.4,
      }}
    >
      Senast
    </Box>
  );
}

function OrganizationOptionCard({ organization, selected, onSelect, isRecent }) {
  const { id, name, logoUrl, roleLabel, isActive, isPrimary, color } = organization;

  const theme = useTheme();

  const primaryMainColor = theme.palette.primary.main;
  const accentColor = primaryMainColor;  // color || "#1976d2";

  const radioRef = useRef(null);

  return (
    <Box
      onClick={() => {
        onSelect(id);
        radioRef.current?.focus();
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: 1.5,
        px: 1.5,
        py: 1.05,
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        userSelect: "none",
        transition: "all .18s cubic-bezier(.4,0,.2,1)",
        background: selected ? "rgba(15,23,42,0.04)" : "transparent",
        "&:hover": {
          background: "rgba(15,23,42,0.055)",
          transform: "translateY(-1px)",
          boxShadow: "0 8px 22px rgba(15,23,42,0.08)",
        },
        "&:hover .org-arrow": {
          opacity: 1,
          transform: "translateX(0)",
        },
        "&:hover .org-check": {
          transform: "scale(1.1)",
        },
      }}
    >
      <Box
        sx={{
          width: 2,
          height: 20,
          borderRadius: 1,
          //background: accentColor,
          opacity: selected ? 1 : 0,
          mr: 0.1,
          transition: "all .25s ease",
          transform: selected ? "scaleY(1)" : "scaleY(0.6)",
          flexShrink: 0,
        }}
      />

      <Radio
        inputRef={radioRef}
        value={id}
        size="small"
        onClick={(e) => e.stopPropagation()}
        sx={{
          mr: 0.1,
          color: "rgba(15,23,42,0.2)",
          "&.Mui-checked": {
            color: accentColor,
            transform: "scale(1.05)",
          },
        }}
      />

      <Avatar
        src={logoUrl || undefined}
        sx={{
          width: 30,
          height: 30,
          fontSize: 12,
          bgcolor: "rgba(15,23,42,0.05)",
          color: "rgba(15,23,42,0.7)",
          mr: 1.4,
          transition: "all .2s",
          transform: selected ? "scale(1.05)" : "scale(1)",
          flexShrink: 0,
        }}
      >
        {!logoUrl && (getInitials(name) || <BusinessIcon fontSize="small" />)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.6} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: selected ? 600 : 500,
              color: "rgba(15,23,42,0.95)",
            }}
          >
            {name}
          </Typography>

          {isPrimary && <PrimaryBadge />}
          {isRecent && !isPrimary && !selected && <RecentBadge />}

          {!!roleLabel && !isPrimary && (
            <Typography sx={{ fontSize: 11, color: "rgba(15,23,42,0.5)" }}>
              {roleLabel}
            </Typography>
          )}
        </Stack>

        <Typography
          sx={{
            fontSize: 12.2,
            mt: 0.2,
            color: selected
              ? "rgba(15,23,42,0.82)"
              : "rgba(15,23,42,0.55)",
            transition: "all .2s",
          }}
        >
          {isActive
            ? "Aktiv nu"
            : isPrimary
              ? "Detta är din ordinarie organisation."
              : "Du kan tillfälligt arbeta i denna organisation och använda dess inställningar och utseende."}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, flexShrink: 0 }}>
        {false &&
          <ArrowForwardRoundedIcon
            className="org-arrow"
            sx={{
              fontSize: 16,
              color: "rgba(15,23,42,0.35)",
              opacity: 0,
              transform: "translateX(-6px)",
              transition: "all .2s ease",
            }}
          />
        }

        <CheckCircleRoundedIcon
          className="org-check"
          sx={{
            fontSize: 18,
            color: isActive ? accentColor : "transparent",
            opacity: isActive ? 1 : 0,
            transform: isActive ? "scale(1)" : "scale(0.7)",
            transition: "all .25s cubic-bezier(.4,0,.2,1)",
          }}
        />
      </Box>
    </Box>
  );
}

OrganizationOptionCard.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    logoUrl: PropTypes.string,
    roleLabel: PropTypes.string,
    isPrimary: PropTypes.bool,
    color: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  isRecent: PropTypes.bool,
};

OrganizationOptionCard.defaultProps = {
  selected: false,
  isRecent: false,
};

OrganizationOptionCard.displayName = "OrganizationOptionCard";





export default function OrganizationSwitchDialog({
  open,
  organizations,
  activeOrganizationId,
  selectionHistory: recentIds,
  onCancel,
  onSelect,
}) {
  const theme = useTheme();

  const primaryMainColor = theme.palette.primary.main;

  const primary = useMemo(
    () => organizations.find(o => o.isPrimary) || organizations[0],
    [organizations]
  );

  const [selectedId, setSelectedId] = useState(
    activeOrganizationId || primary?.id
  );
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    setSelectedId(activeOrganizationId || primary?.id);
  }, [activeOrganizationId, primary]);

  const sorted = useMemo(() => {
    const recentSet = new Set(recentIds.map(String));
    const primaryArr = organizations.filter(o => o.isPrimary);
    const recent = organizations.filter(
      o => !o.isPrimary && recentSet.has(String(o.id))
    );
    const rest = organizations.filter(
      o => !o.isPrimary && !recentSet.has(String(o.id))
    );
    return [...primaryArr, ...recent, ...rest];
  }, [organizations, recentIds]);

  const handleContinue = () => {
    if (!selectedId) return;

    onSelect?.(selectedId, remember);
  };

  const selectedOrganization = organizations.find(
    o => o.id === selectedId
  );

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 30px 90px rgba(15,23,42,0.25)",
          transform: "scale(0.98)",
          animation: "dialogIn .18s ease forwards",
          "@keyframes dialogIn": {
            to: { transform: "scale(1)" }
          }
        }
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 1.2 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
          Välj organisation
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(15,23,42,0.7)", mt: 0.4 }}>
          Du är medlem i flera organisationer. Välj vilken organisation du vill arbeta i just nu.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 0.5 }}>
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(25,118,210,0.05)",
            border: "1px solid rgba(25,118,210,0.12)",
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: "rgba(15,23,42,0.82)" }}>
            När du byter organisation används den organisationens färger, logotyp, menyer och inställningar.
          </Typography>
        </Box>

        <RadioGroup
          value={String(selectedId)}
          onChange={(e) => setSelectedId(Number(e.target.value))}
        >
          <Stack spacing={0.25}>
            {sorted.map(org => (
              <OrganizationOptionCard
                key={org.id}
                organization={org}
                selected={selectedId === org.id}
                onSelect={setSelectedId}
                isRecent={recentIds.includes(org.id)}
              />
            ))}
          </Stack>
        </RadioGroup>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ flexWrap: "wrap", pl: 2, pr: 3, pb: 3, flexDirection: "column", justifyContent: "flex-start", alignItems: "stretch" }}>
        <Divider sx={{ mb: 2.4, opacity: 0.6 }} />

        {/* Top row */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          {/* Selected org */}
          <Box
            sx={{
              fontSize: 13,
              color: "rgba(15,23,42,0.6)",
            }}
          >
            Vald organisation:<br />
            <Box
              component="span"
              sx={{
                fontWeight: 600,
                color: "#111",
              }}
            >
              {selectedOrganization?.name || "-"}
            </Box>
          </Box>

          {/* Remember my selection */}
          <FormControlLabel
            control={
              <Checkbox
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
                sx={{
                  p: 0.5,
                  color: "rgba(15,23,42,0.4)",
                  "&.Mui-checked": {
                    color: primaryMainColor /* "#7a1f3d" */
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: 13,
                  color: "rgba(15,23,42,0.75)",
                  whiteSpace: "nowrap",
                }}
              >
                Kom ihåg mitt val på den här enheten
              </Typography>
            }
            sx={{ m: 0 }}
          />
        </Stack>

        {/* Actions */}
        <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: 2.5 }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{
              minWidth: 110,
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 600,
              borderColor: "rgba(15,23,42,0.15)",
              color: "rgba(15,23,42,0.75)",

              "&:hover": {
                background: "rgba(15,23,42,0.04)",
                borderColor: "rgba(15,23,42,0.25)",
              },
            }}
          >
            Avbryt
          </Button>

          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={!selectedId}
            sx={{
              minWidth: 130,
              borderRadius: 1,
              textTransform: "none",
              fontWeight: 700,
              background: primaryMainColor /* "#7a1f3d" */,

              boxShadow: "0 6px 18px rgba(122,31,61,0.25)",

              "&:hover": {
                background: "#651a32",
                boxShadow: "0 10px 26px rgba(122,31,61,0.35)",
                transform: "translateY(-1px)",
              },

              "&:active": {
                transform: "translateY(0)",
                boxShadow: "0 4px 12px rgba(122,31,61,0.25)",
              },

              "&.Mui-disabled": {
                background: "rgba(15,23,42,0.2)",
                color: "#fff",
              },
            }}
          >
            Fortsätt
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

OrganizationSwitchDialog.propTypes = {
  open: PropTypes.bool,
  organizations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      logoUrl: PropTypes.string,
      roleLabel: PropTypes.string,
      isPrimary: PropTypes.bool,
      color: PropTypes.string,
    })
  ),
  activeOrganizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onCancel: PropTypes.func,
  onSelect: PropTypes.func,
  selectionHistory: PropTypes.arrayOf(PropTypes.number),
};

OrganizationSwitchDialog.defaultProps = {
  open: false,
  organizations: [],
  activeOrganizationId: "",
  onCancel: undefined,
  onSelect: undefined,
  selectionHistory: [],
};

OrganizationSwitchDialog.displayName = "OrganizationSwitchDialog";
